import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'clsx';
import { Range } from 'rc-slider';

import styles from './range-slider.scss';

interface RangeSliderProps {
  max: number;
  min: number;
  onBeforeChange?: (value: [number, number]) => void;
  onChange: (value: [number, number]) => void;
  values?: number[];
}

function RangeSlider(props: RangeSliderProps) {
  const { max, min, values = [min, max], onBeforeChange, onChange } = props;
  const [pushable, setPushable] = useState(-1);
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const providedMaxValue = values[1] > max || values[1] < min ? max : values[1];
  const providedMinValue =
    values[0] > providedMaxValue || values[0] < min ? min : values[0];
  const updatePushable = useCallback(() => {
    setTimeout(() => {
      const sliderHandle =
        sliderRef?.current?.querySelector('.rc-slider-handle');
      const sliderWidth =
        sliderRef?.current?.getBoundingClientRect().width || 0;
      const handleWidth = sliderHandle?.getBoundingClientRect().width || 0;
      const stepWidth = sliderWidth / (max - min);
      const newPushable = Math.ceil(handleWidth / stepWidth);

      if (newPushable !== pushable) {
        if (providedMaxValue - providedMinValue < newPushable) {
          const diff = newPushable - (providedMaxValue - providedMinValue);
          const half = diff / 2;
          if (
            providedMinValue - half >= min &&
            providedMaxValue + half <= max
          ) {
            onChange([providedMinValue - half, providedMaxValue + half]);
          } else if (providedMinValue - diff >= min) {
            onChange([providedMinValue - diff, providedMaxValue]);
          } else if (providedMaxValue + diff <= max) {
            onChange([providedMinValue, providedMaxValue + diff]);
          }
        }
        setPushable(newPushable);
      }
    }, 100);
  }, [max, min, onChange, providedMaxValue, providedMinValue, pushable]);

  // useRef doesn't trigger a render, so we useCallback to trigger a render and
  // update the sliderRef to refer to it later.
  const sliderRefCallback = useCallback(
    node => {
      if (node !== null) {
        sliderRef.current = node;
        if (pushable === -1) {
          updatePushable();
        }
      }
    },
    [pushable, updatePushable]
  );

  useEffect(() => {
    window.addEventListener('resize', updatePushable);
    return () => window.removeEventListener('resize', updatePushable);
  }, [updatePushable]);

  return (
    <div
      className={cn(styles['range-slider'], 'range-slider')}
      ref={sliderRefCallback}
    >
      <Range
        allowCross={false}
        defaultValue={[providedMinValue, providedMaxValue]}
        max={max}
        min={min}
        onBeforeChange={vals => {
          updatePushable();
          if (onBeforeChange) {
            onBeforeChange(vals);
          }
        }}
        onChange={onChange}
        pushable={pushable}
        value={values}
      />
    </div>
  );
}

export default RangeSlider;
